import { Sorter } from '../../components/Sorter/Sorter'
import { SortOrder } from 'antd/es/table/interface'
import { TAdditionalValue, TAdditionalValuesTablesColumnsFunc } from './types'
import { currencyFormatter } from '../ContractDetails/ContractDetailsGeneral/ContractDetailsForm/utils'
import { DescriptionCell } from '../../components/DescriptionCell/DescriptionCell'
import { ColumnsType } from 'antd/es/table'
import { timelineDateFormat } from '../../components/Timelines/constants'
import moment from 'moment'
import IconNameItem from '../../components/IconNameItem/IconNameItem'
import { ROLES_NAMES } from '../../constants'
import { AdditionalValuesTableMenu } from './Menu/Menu'
import { ROLES } from 'features/Permission'

export const columns = ({
  typesList,
  subtypesList,
  isAbleToManage,
  user,
  canSeeHealthSystem
}: TAdditionalValuesTablesColumnsFunc): ColumnsType<TAdditionalValue> => {
  const tableCols: ColumnsType<TAdditionalValue> = [
    {
      dataIndex: 'name',
      key: 'name',
      width: 228,
      title: 'Name'
    },
    {
      dataIndex: 'type',
      key: 'type',
      width: 160,
      title: 'Type',
      sorter: true,
      showSorterTooltip: false,
      render: (_, { type }) =>
        typesList.find((t) => t.value === type)?.label || '-'
    },
    {
      dataIndex: 'subtype',
      key: 'subtype',
      width: 160,
      title: 'Subtype',
      sorter: true,
      showSorterTooltip: false,
      render: (_, { subtype }) =>
        subtypesList.find((sub) => sub.value === subtype)?.label || '-'
    },
    {
      dataIndex: 'description',
      key: 'description',
      width: 240,
      title: 'Description',
      render: (_, { description }) => (
        <DescriptionCell content={description} text={description} />
      ),
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'amount_usd',
      key: 'amount_usd',
      width: 168,
      render: (_, { amount_usd }) => currencyFormatter(amount_usd),
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<TAdditionalValue>
          dataIndex="amount_usd"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Amount, $
        </Sorter>
      ),
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'health_system',
      key: 'health_system',
      width: 228,
      title: 'Health system',
      render: (_, { health_system }) => health_system.name,
      showSorterTooltip: false
    },
    {
      dataIndex: 'hospital',
      key: 'hospital',
      width: 228,
      title: 'Hospital',
      render: (_, { hospital }) => hospital?.name ?? '-',
      showSorterTooltip: false
    },
    {
      dataIndex: 'category',
      key: 'category',
      width: 288,
      title: 'Associated category',
      render: (_, { category }) => category?.name ?? '-',
      showSorterTooltip: false
    },
    {
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 128,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<TAdditionalValue>
          dataIndex="updated_at"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Date
        </Sorter>
      ),
      render: (_, { created_at, updated_at }) =>
        updated_at
          ? moment(updated_at).format(timelineDateFormat)
          : created_at
          ? moment(created_at).format(timelineDateFormat)
          : '-',
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'sourcing_person',
      key: 'sourcing_person',
      width: 192,
      title: 'Sourcing owner',
      render: (_, { sourcing_person }) =>
        sourcing_person?.uuid ? (
          <div
            key={sourcing_person.uuid}
            className="separate-item categories-responsible"
          >
            <IconNameItem
              firstName={sourcing_person.first_name}
              className="responsible"
              lastName={sourcing_person.last_name}
              src={sourcing_person.avatar?.file_preview || null}
              roleName={ROLES_NAMES[sourcing_person.role]}
            />
          </div>
        ) : (
          <div className="separate-item row align-center">-</div>
        )
    },
    {
      dataIndex: 'created_by',
      key: 'created_by',
      width: 192,
      title: 'Entered by',
      render: (_, { created_by }) =>
        created_by?.uuid ? (
          <div
            key={created_by.uuid}
            className="separate-item categories-responsible"
          >
            <IconNameItem
              firstName={created_by.first_name}
              className="responsible"
              lastName={created_by.last_name}
              src={created_by.avatar?.file_preview || null}
              roleName={ROLES_NAMES[created_by.role]}
            />
          </div>
        ) : (
          <div className="separate-item row align-center">-</div>
        )
    }
  ]
  if (isAbleToManage) {
    tableCols.push({
      dataIndex: 'action',
      key: 'action',
      title: '',
      width: 48,
      fixed: 'right',
      render: (_, row) => {
        const canEditValues =
          user.role === ROLES.CONTRACT_STEWARD
            ? isAbleToManage && user.uuid === row.created_by.uuid
            : isAbleToManage
        return canEditValues ? <AdditionalValuesTableMenu row={row} /> : null
      },
      showSorterTooltip: false
    })
  }

  return canSeeHealthSystem
    ? tableCols
    : tableCols.filter((col) => col.key !== 'health_system')
}
